import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, CalendarDays, ListChecks, Users, UserPlus, Settings, LogOut, Home, Compass, Activity, GraduationCap, Menu, X, BarChart3, LifeBuoy, Inbox, Tablet, UserCheck, Smartphone, Star, Megaphone, LayoutDashboard, Layers3, Moon, Sun, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { useBranding } from "@/lib/branding";
import GuidedTour from "@/components/GuidedTour";
import { stepsForRole } from "@/lib/sopSteps";
import { setDashboardAuthToken, useTrackView } from "@/lib/analyticsTracker";
import OverviewPanel from "@/pages/admin/OverviewPanel";
import ServiceQueuePanel from "@/pages/admin/ServiceQueuePanel";
import ModulesPanel from "@/pages/admin/ModulesPanel";
import UnifiedBookingCalendar from "@/pages/admin/UnifiedBookingCalendar";
import CrewPanel from "@/pages/admin/CrewPanel";
import UsersPanel from "@/pages/admin/UsersPanel";
import CabinsPanel from "@/pages/admin/CabinsPanel";
import TripsPanel from "@/pages/admin/TripsPanel";
import SettingsPanel from "@/pages/admin/SettingsPanel";
import StatisticsPanel from "@/pages/admin/StatisticsPanel";
import PlatformToolsPanel from "@/pages/admin/PlatformToolsPanel";
import SupportPanel from "@/pages/admin/SupportPanel";
import DevSupportPanel from "@/pages/admin/DevSupportPanel";
import DeviceLabelBadge from "@/components/DeviceLabelBadge";
import ConnectedDevicesPanel from "@/pages/admin/ConnectedDevicesPanel";
import CalendarDevicesPanel from "@/pages/admin/CalendarDevicesPanel";
import AppleWatchPanel from "@/pages/admin/AppleWatchPanel";
import GuestsOnboardPanel from "@/pages/admin/GuestsOnboardPanel";
import RegularsPanel from "@/pages/admin/RegularsPanel";
import WhatsNewPanel from "@/pages/admin/WhatsNewPanel";
import WhatsNewInbox from "@/components/WhatsNewInbox";
import WhatsNewBell from "@/components/WhatsNewBell";

const OPS_ROLES = ["developer", "admin", "manager", "supervisor", "crew"];
const ALL_ROLES = ["developer", "admin", "manager", "supervisor", "crew"];
const USER_MGMT_ROLES = ["developer", "admin", "manager"];
// Yacht-configuration tabs — Supervisor is intentionally excluded so their
// desktop sidebar matches the (already restricted) mobile crew view. The
// backend enforces the same boundary via `_require_manager_role`.
const MANAGER_ROLES = ["developer", "admin", "manager"];

const ALL_TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, roles: ALL_ROLES },
  { key: "queue", label: "Service queue", icon: ListChecks, roles: ALL_ROLES },
  { key: "calendar", label: "All services calendar", icon: CalendarDays, roles: ALL_ROLES },
  { key: "modules", label: "Modules", icon: Layers3, roles: ALL_ROLES },
  { key: "guests_onboard", label: "Guests onboard", icon: UserCheck, roles: ALL_ROLES },
  { key: "trips", label: "Trips & guests", icon: Compass, roles: MANAGER_ROLES },
  { key: "regulars", label: "Regulars", icon: Star, roles: MANAGER_ROLES },
  { key: "cabins", label: "Cabins", icon: Home, roles: MANAGER_ROLES },
  { key: "crew", label: "Crew", icon: Users, roles: MANAGER_ROLES },
  { key: "users", label: "Users", icon: UserPlus, roles: USER_MGMT_ROLES },
  { key: "devices", label: "Connected devices", icon: Tablet, roles: MANAGER_ROLES },
  { key: "calendar_devices", label: "Calendar iPads", icon: CalendarDays, roles: MANAGER_ROLES },
  { key: "apple_watch", label: "Apple Watch", icon: Smartphone, roles: OPS_ROLES },
  { key: "settings", label: "Platform settings", icon: Settings, roles: MANAGER_ROLES },
  { key: "statistics", label: "Statistics", icon: BarChart3, roles: MANAGER_ROLES },
  { key: "support", label: "Support", icon: LifeBuoy, roles: ALL_ROLES },
  { key: "dev_support", label: "Support inbox", icon: Inbox, roles: ["developer"] },
  { key: "whats_new", label: "What's New", icon: Megaphone, roles: ["developer"] },
  { key: "platform", label: "Platform tools", icon: Activity, roles: ["developer"] },
];

const NAV_GROUPS = [
  { key: "operations", label: "Operations", icon: LayoutDashboard, tabs: ["overview", "queue", "calendar"] },
  { key: "guests", label: "Guests & Trips", icon: Users, tabs: ["guests_onboard", "trips", "regulars", "cabins"] },
  { key: "experience", label: "Guest Experience", icon: Layers3, tabs: ["modules"] },
  { key: "crew_access", label: "Crew & Access", icon: UserPlus, tabs: ["crew", "users", "devices", "calendar_devices", "apple_watch"] },
  { key: "management", label: "Management", icon: Settings, tabs: ["statistics", "settings", "support"] },
  { key: "developer", label: "Developer", icon: Activity, tabs: ["dev_support", "whats_new", "platform"] },
];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const { spaName, boatName, logoUrl, adminLogoUrl } = useBranding();
  const navigate = useNavigate();

  const role = admin?.role || "admin";
  const tabs = ALL_TABS.filter((t) => t.roles.includes(role));
  // Persist the selected tab across full page reloads so operators land back
  // on Trips / Cabins / etc. instead of getting bounced to the default
  // Calendar tab every refresh. Scoped to the current admin so different
  // roles on the same iPad don't step on each other's last-tab.
  const tabStorageKey = admin?.id ? `yspa:adminTab:${admin.id}` : "yspa:adminTab";
  const [tab, setTab] = useState(() => {
    try {
      const stored = typeof window !== "undefined" ? window.localStorage.getItem(tabStorageKey) : null;
      if (stored && tabs.some((t) => t.key === stored)) return stored;
    } catch (_e) { /* localStorage disabled — fall through */ }
    return tabs[0]?.key || "calendar";
  });
  const [sidebarOpen, setSidebarOpen] = useState(false); // controls mobile drawer
  const [openNavGroup, setOpenNavGroup] = useState(null);
  const themeStorageKey = admin?.id ? `slx:adminTheme:${admin.id}` : "slx:adminTheme";
  const [darkMode, setDarkMode] = useState(() => {
    try { return window.localStorage.getItem(themeStorageKey) === "dark"; }
    catch (_e) { return false; }
  });

  const visibleNavGroups = NAV_GROUPS.map((g) => ({ ...g, children: g.tabs.map((key) => tabs.find((t) => t.key === key)).filter(Boolean) })).filter((g) => g.children.length);
  const activeNavGroup = visibleNavGroups.find((g) => g.children.some((c) => c.key === tab))?.key || null;

  useEffect(() => { if (activeNavGroup) setOpenNavGroup(activeNavGroup); }, [activeNavGroup]);
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", darkMode);
    root.style.colorScheme = darkMode ? "dark" : "light";
    try { window.localStorage.setItem(themeStorageKey, darkMode ? "dark" : "light"); } catch (_e) {}
    return () => { root.classList.remove("dark"); root.style.colorScheme = ""; };
  }, [darkMode, themeStorageKey]);

  // Whenever the tab changes, remember it. Kept in an effect so the write
  // survives even if setTab is called from a source other than pickTab.
  useEffect(() => {
    try { window.localStorage.setItem(tabStorageKey, tab); } catch (_e) { /* ignore */ }
  }, [tab, tabStorageKey]);

  // Analytics — mount the dashboard auth token on the tracker so all
  // subsequent events are attributed to this crew user. Also emit a
  // view_enter/exit for each admin panel so the Developer analytics
  // report can show "where the crew spends their time".
  useEffect(() => {
    setDashboardAuthToken(null); // cookie-auth build: retained API compatibility only
    return () => setDashboardAuthToken(null);
  }, []);
  // Track the currently-selected admin tab as a distinct "view" so
  // Feature Analytics can show per-panel dwell time. We reuse the
  // shared `useTrackView` (rather than a bespoke component-local ref)
  // because a single ref shared across all `admin:${tab}` features
  // dropped every view_exit when the crew switched tabs — the module
  // Map inside useTrackView correctly scopes ownership per feature.
  useTrackView(tab ? `admin:${tab}` : null);

  // If the persisted tab is no longer available for this role (e.g. role
  // downgrade), snap back to the first allowed tab so we never render a
  // blank main panel.
  useEffect(() => {
    if (!tabs.some((t) => t.key === tab) && tabs[0]) setTab(tabs[0].key);
  }, [tabs, tab]);

  const doLogout = async () => {
    try { window.localStorage.removeItem(tabStorageKey); } catch (_e) { /* ignore */ }
    await logout();
    navigate("/admin/login");
  };
  const [tourOpen, setTourOpen] = useState(false);
  const tourSteps = stepsForRole(role);
  const { boatName: brandBoat } = useBranding();

  // Close the drawer whenever the tab changes on mobile.
  const pickTab = (k) => { setTab(k); setSidebarOpen(false); };

  // Cross-panel navigation intents — the Cabins page dispatches these when
  // an operator clicks a berth (free berth = "add guest to this cabin",
  // occupied berth = "highlight this guest in Trips"). We hop over to the
  // Trips tab; TripsPanel picks the payload up from window.__yspaIntent on
  // its next render and consumes+clears it. Using a window prop rather than
  // React context keeps the wiring shallow across three unrelated panels.
  useEffect(() => {
    const onIntent = (ev) => {
      const intent = ev?.detail;
      if (!intent || !intent.kind) return;
      window.__yspaIntent = intent;
      // Snap to Trips whenever the intent targets a trip/guest workflow.
      if (["add_guest_to_cabin", "highlight_guest", "schedule_move_to_cabin"].includes(intent.kind)) {
        setTab("trips");
      }
      // Nudge listeners already mounted (TripsPanel) so they consume even
      // when the tab was already showing.
      window.dispatchEvent(new CustomEvent("yspa:intent-ready"));
    };
    window.addEventListener("yspa:intent", onIntent);
    return () => window.removeEventListener("yspa:intent", onIntent);
  }, []);
  // Lock body scroll while the drawer is open (mobile).
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const currentTabLabel = tabs.find((t) => t.key === tab)?.label || "";

  return (
    <div className="min-h-screen bg-background md:flex">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between gap-3 px-4 h-14 border-b border-border bg-card" data-testid="admin-mobile-header">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 hover:bg-muted rounded"
          aria-label="Open menu"
          data-testid="admin-menu-btn"
        >
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          {logoUrl
            ? <img src={logoUrl} alt="" className="h-6 w-6 object-contain flex-shrink-0" />
            : <Anchor className="h-4 w-4 text-primary flex-shrink-0" strokeWidth={1.5} />}
          <div className="min-w-0">
            <div className="font-display text-sm leading-none truncate">SLX-one</div>
            <div className="overline text-primary/60 text-[9px] leading-none mt-0.5 truncate">{currentTabLabel}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setDarkMode((v) => !v)} className="h-8 w-8 p-0 rounded-full" aria-label={darkMode ? "Light mode" : "Dark mode"}>
            {darkMode ? <Sun className="h-3.5 w-3.5"/> : <Moon className="h-3.5 w-3.5"/>}
          </Button>
          <Button variant="outline" size="sm" onClick={doLogout} className="h-8 w-8 p-0 rounded-full" aria-label="Sign out" data-testid="admin-logout-mobile"><LogOut className="h-3.5 w-3.5"/></Button>
          <WhatsNewBell />
        </div>
      </header>

      {/* Backdrop for the mobile drawer */}
      {sidebarOpen && (
        <button
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden animate-in fade-in"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close menu"
          data-testid="admin-sidebar-backdrop"
        />
      )}

      {/* Sidebar — permanent on md+, slide-out drawer on mobile.
          `md:sticky md:top-0 md:h-screen` pins the panel to the viewport
          so the SOP-training / e-mail / device / sign-out footer stays
          visible regardless of how tall the main content is — the
          nav list itself scrolls internally via `flex-1 overflow-y-auto`
          when the tab list is longer than the viewport (dev role). */}
      <aside
        className={`
          fixed md:sticky md:top-0 md:h-screen inset-y-0 left-0 z-50
          w-72 md:w-64 border-r border-border bg-card flex-shrink-0 flex flex-col
          transform transition-transform duration-200
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
        `}
        data-testid="admin-sidebar"
      >
        <div className="p-6 border-b border-border flex items-start justify-between">
          <div className="flex items-center gap-3">
            {adminLogoUrl ? <img src={adminLogoUrl} alt="Logo" className="h-7 w-7 object-contain" data-testid="admin-sidebar-logo" /> : <Anchor className="h-5 w-5 text-primary" strokeWidth={1.5} />}
            <div>
              <div className="font-display text-lg leading-tight">SLX-one</div>
              <div className="overline text-primary/50 text-[10px] mt-0.5">{boatName}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <WhatsNewBell />
            <button
              className="md:hidden p-1 -mr-1 hover:bg-muted rounded"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
              data-testid="admin-sidebar-close"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-2 overflow-y-auto">
          {visibleNavGroups.map((group) => {
            const GroupIcon = group.icon;
            const expanded = openNavGroup === group.key;
            const groupActive = group.children.some((child) => child.key === tab);
            return <div key={group.key}>
              <button type="button" onClick={() => setOpenNavGroup((cur) => cur === group.key ? null : group.key)} className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${groupActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"} hover:bg-muted/70`} aria-expanded={expanded}>
                <GroupIcon className={`h-[18px] w-[18px] flex-shrink-0 ${groupActive ? "text-accent" : ""}`} strokeWidth={1.5}/>
                <span className="flex-1 text-sm font-medium">{group.label}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} strokeWidth={1.5}/>
              </button>
              {expanded && <div className="ml-5 mt-1 pl-3 border-l border-border space-y-1">{group.children.map((t) => { const Icon=t.icon; const active=tab===t.key; return <button key={t.key} onClick={() => pickTab(t.key)} data-testid={`tab-${t.key}`} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-sm transition-colors ${active ? "bg-accent/10 text-accent font-medium" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}><Icon className="h-4 w-4" strokeWidth={1.5}/><span>{t.label}</span></button>})}</div>}
            </div>;
          })}
        </nav>
        <div className="p-4 border-t border-border space-y-2">
          <Button variant="outline" onClick={() => setDarkMode((v) => !v)} className="w-full rounded-full justify-start" data-testid="admin-theme-toggle">{darkMode ? <Sun className="h-3.5 w-3.5 mr-2"/> : <Moon className="h-3.5 w-3.5 mr-2"/>}{darkMode ? "Light mode" : "Dark mode"}</Button>
          <Button
            variant="outline"
            onClick={() => navigate("/crew")}
            className="w-full rounded-full border-accent/40 text-accent hover:bg-accent/5 md:hidden"
            data-testid="mobile-crew-launch"
            title="Switch to the iPhone-friendly crew view"
          >
            <Smartphone className="h-3.5 w-3.5 mr-2" /> Mobile crew view
          </Button>
          <Button
            variant="outline"
            onClick={() => { setTourOpen(true); setSidebarOpen(false); }}
            className="w-full rounded-full border-accent/40 text-accent hover:bg-accent/5"
            data-testid="sop-training-btn"
          >
            <GraduationCap className="h-3.5 w-3.5 mr-2" /> SOP training
          </Button>
          <div className="text-xs text-muted-foreground mb-1 truncate pt-2 border-t border-border">{admin?.email}</div>
          <div className="overline text-primary/50 text-[10px] mb-1">{role}</div>
          <DeviceLabelBadge />
          <Button variant="outline" onClick={doLogout} className="w-full rounded-full" data-testid="admin-logout">
            <LogOut className="h-3 w-3 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="p-4 sm:p-6 md:p-10 lg:p-12" data-testid="admin-main">
          {tab === "overview" && <OverviewPanel />}
          {tab === "queue" && <ServiceQueuePanel />}
          {tab === "modules" && <ModulesPanel />}
          {tab === "calendar" && <UnifiedBookingCalendar />}
          {tab === "guests_onboard" && <GuestsOnboardPanel />}
          {tab === "trips" && <TripsPanel />}
          {tab === "regulars" && <RegularsPanel />}
          {tab === "cabins" && <CabinsPanel />}
          {tab === "crew" && <CrewPanel />}
          {tab === "users" && <UsersPanel />}
          {tab === "devices" && <ConnectedDevicesPanel />}
          {tab === "calendar_devices" && <CalendarDevicesPanel />}
          {tab === "apple_watch" && <AppleWatchPanel />}
          {tab === "settings" && <SettingsPanel />}
          {tab === "statistics" && <StatisticsPanel />}
          {tab === "support" && <SupportPanel />}
          {tab === "dev_support" && <DevSupportPanel />}
          {tab === "whats_new" && <WhatsNewPanel />}
          {tab === "platform" && <PlatformToolsPanel />}
        </div>
      </main>
      <WhatsNewInbox />
      <GuidedTour open={tourOpen} steps={tourSteps} onClose={() => setTourOpen(false)} brand={brandBoat || "Carinthia VII"} />
    </div>
  );
}
